import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

import PeopleIcon from '@mui/icons-material/People';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import RouteIcon from '@mui/icons-material/Route';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrafficIcon from '@mui/icons-material/Traffic';
import SpeedIcon from '@mui/icons-material/Speed';
import BlockIcon from '@mui/icons-material/Block';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';

const ICON_MAP = {
  boom: TrafficIcon,
  cd: PeopleIcon,
  vd: DirectionsCarIcon,
  pd: PersonIcon,
  id: SecurityIcon,
  vc: TrafficIcon,
  ls: LocalShippingIcon,
  or: RouteIcon,
  oa: BlockIcon,
  ta: WarningAmberIcon,
  usp: WarningAmberIcon,
  os: SpeedIcon,
  ct: VideocamOffIcon,
  sh: WarningAmberIcon,
  ii: WbIncandescentIcon,
};

const COLOR_MAP = {
  boom: '#E67E22',
  cd: '#1ABC9C',
  vd: '#E74C3C',
  pd: '#3498DB',
  id: '#FFC107',
  vc: '#9B59B6',
  ls: '#F39C12',
  or: '#4ECDC4',
  oa: '#FF1744',
  ta: '#E67E22',
  usp: '#E67E22',
  os: '#E67E22',
  ct: '#8E44AD',
  sh: '#E67E22',
  ii: '#95A5A6',
};


const CompactAlertCard = ({ id, title, count = 0, selected = false, onClick, isVts = false, compact = false, fillHeight = false }) => {
  const IconComponent = ICON_MAP[id] || SecurityIcon;
  const accentColor = COLOR_MAP[id] || '#1ABC9C';
  const hasEvents = count > 0;

  return (
    <Tooltip title={`${title} – ${count} event${count !== 1 ? 's' : ''}`} placement="top" arrow>
      <Box
        onClick={onClick}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: selected
            ? 'linear-gradient(135deg, rgba(26,188,156,0.18) 0%, rgba(26,188,156,0.06) 100%)'
            : hasEvents
              ? `linear-gradient(135deg, ${accentColor}1f 0%, rgba(22,33,46,0.96) 70%)`
              : 'linear-gradient(135deg, #1f2b39 0%, #1a2532 100%)',
          border: selected
            ? `1.5px solid #1ABC9C`
            : hasEvents
              ? `1.5px solid ${accentColor}aa`
              : '1.5px solid rgba(255,255,255,0.06)',
          borderRadius: '14px',
          p: compact ? '8px 12px' : '14px 16px',
          minHeight: compact ? 52 : 86,
          height: fillHeight ? '100%' : 'auto',
          boxSizing: 'border-box',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
          boxShadow: selected
            ? `0 6px 18px rgba(26,188,156,0.22)`
            : hasEvents
              ? `0 4px 14px ${accentColor}22`
              : '0 2px 8px rgba(0,0,0,0.25)',
          // Colored accent strip down the left edge
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: hasEvents || selected ? accentColor : 'rgba(255,255,255,0.08)',
            opacity: hasEvents || selected ? 1 : 0.5,
            transition: 'background-color 0.2s, opacity 0.2s',
          },
          '&:hover': {
            border: selected ? `1.5px solid #1ABC9C` : `1.5px solid ${accentColor}`,
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 22px ${accentColor}3a`,
          },
          // Pulse animation for cards with new events
          ...(hasEvents && !selected && {
            animation: 'cardPulse 2.5s ease-in-out infinite',
            '@keyframes cardPulse': {
              '0%': { boxShadow: `0 4px 14px ${accentColor}22` },
              '50%': { boxShadow: `0 4px 20px ${accentColor}4d` },
              '100%': { boxShadow: `0 4px 14px ${accentColor}22` },
            }
          })
        }}
      >
        {/* Left: count + label */}
        <Box sx={{ display: 'flex', flexDirection: compact ? 'row' : 'column', alignItems: compact ? 'center' : 'flex-start', gap: compact ? 1 : 0, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"Roboto Condensed", "Roboto", sans-serif',
              fontSize: compact ? '1.1rem' : '2rem',
              fontWeight: 700,
              lineHeight: 1,
              color: hasEvents ? accentColor : '#4a6080',
              letterSpacing: '-0.5px',
              flexShrink: 0,
              transition: 'color 0.2s',
            }}
          >
            {count > 999 ? '999+' : count}
          </Typography>
          <Typography
            sx={{
              fontSize: compact ? '0.6rem' : '0.68rem',
              fontWeight: 500,
              color: selected ? '#1ABC9C' : '#8fa8c0',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              mt: compact ? 0 : 0.3,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Right: icon circle */}
        <Box
          sx={{
            width: compact ? 32 : 44,
            height: compact ? 32 : 44,
            borderRadius: '50%',
            backgroundColor: hasEvents ? `${accentColor}22` : 'rgba(255,255,255,0.04)',
            border: `2px solid ${hasEvents ? accentColor : 'rgba(255,255,255,0.06)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.2s',
          }}
        >
          <IconComponent
            sx={{
              fontSize: compact ? '1rem' : '1.35rem',
              color: hasEvents ? accentColor : '#3d5166',
              transition: 'color 0.2s',
            }}
          />
        </Box>

        {/* Selected indicator dot */}
        {selected && (
          <Box
            sx={{
              position: 'absolute',
              top: 7,
              left: 7,
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: '#1ABC9C',
              boxShadow: '0 0 6px #1ABC9C',
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

export default CompactAlertCard;
